// Wrappers para /api/quizzes/* y /api/attempts/*.
// Tipos paralelos a apps/api/src/quizzes/types.ts.

import { apiFetch } from './api';

export type AnswerType =
  | 'single'
  | 'multiple'
  | 'sort_answer'
  | 'matrix_sort_answer'
  | 'cloze_answer';

export interface SingleMultipleOption {
  id: string;
  text: string;
  is_correct?: boolean; // solo presente al ver soluciones (admin / attempt finalizado)
}

export interface SortItem {
  id: string;
  text: string;
  correct_position?: number;
}

export interface MatrixSide {
  id: string;
  text: string;
}

export interface MatrixMatch {
  left_id: string;
  right_id: string;
}

export interface ClozeBlank {
  accepted: string[];
}

export interface PlayerSinglePayload {
  options: SingleMultipleOption[];
}

export interface PlayerSortPayload {
  items: SortItem[];
}

export interface PlayerMatrixSortPayload {
  left: MatrixSide[];
  right: MatrixSide[];
  matches?: MatrixMatch[];
}

export interface PlayerClozePayload {
  template: string;
  blank_count?: number;
  blanks?: ClozeBlank[];
}

export interface QuizMeta {
  id: number;
  title: string;
  legacy_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface PlayerQuestion {
  id: number;
  position: number;
  question_text: string;
  answer_type: AnswerType;
  points: number;
  payload:
    | PlayerSinglePayload
    | PlayerSortPayload
    | PlayerMatrixSortPayload
    | PlayerClozePayload
    | null;
}

export interface QuizPlayer {
  quiz: QuizMeta;
  questions: PlayerQuestion[];
}

export async function getQuizForPlayer(quizId: number): Promise<QuizPlayer> {
  const r = await apiFetch<{ status: 'ok'; quiz: QuizMeta; questions: PlayerQuestion[] }>(`/quizzes/${quizId}`);
  return { quiz: r.quiz, questions: r.questions };
}

export async function startAttempt(quizId: number, lessonId?: number): Promise<number> {
  const r = await apiFetch<{ status: 'ok'; attempt_id: number }>(
    `/quizzes/${quizId}/attempts`,
    { method: 'POST', body: JSON.stringify(lessonId ? { lesson_id: lessonId } : {}) }
  );
  return r.attempt_id;
}

export interface SubmitAnswerInput {
  question_id: number;
  answer_type: AnswerType;
  data:
    | { selected_id: string }
    | { selected_ids: string[] }
    | { ordered_ids: string[] }
    | { matches: MatrixMatch[] }
    | { values: string[] };
}

export async function submitAnswer(attemptId: number, input: SubmitAnswerInput): Promise<void> {
  await apiFetch(`/attempts/${attemptId}/answers`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface FinishResult {
  score: number;
  max_score: number;
}

export async function finishAttempt(attemptId: number): Promise<FinishResult> {
  const r = await apiFetch<{ status: 'ok'; score: number; max_score: number }>(
    `/attempts/${attemptId}/finish`,
    { method: 'POST' }
  );
  return { score: r.score, max_score: r.max_score };
}

export interface AttemptDetail {
  attempt: {
    id: number;
    quiz_id: number;
    user_id: number;
    lesson_id: number | null;
    started_at: string;
    finished_at: string | null;
    score: number;
    max_score: number;
  };
  answers: {
    id: number;
    attempt_id: number;
    question_id: number;
    answer_payload: unknown;
    is_correct: number | null;
    points_earned: number;
    answered_at: string;
  }[];
  questions: PlayerQuestion[];
}

export async function getAttempt(attemptId: number): Promise<AttemptDetail> {
  const r = await apiFetch<{ status: 'ok' } & AttemptDetail>(`/attempts/${attemptId}`);
  return { attempt: r.attempt, answers: r.answers, questions: r.questions };
}

// --- Admin ---

export interface AdminQuestion {
  id?: number;
  position: number;
  question_text: string;
  answer_type: AnswerType;
  points: number;
  payload: unknown;
}

export interface AdminQuizDetail {
  quiz: QuizMeta;
  questions: AdminQuestion[];
}

export async function adminListQuizzes(): Promise<QuizMeta[]> {
  const r = await apiFetch<{ status: 'ok'; items: QuizMeta[] }>('/quizzes/admin');
  return r.items;
}

export async function adminGetQuiz(id: number): Promise<AdminQuizDetail> {
  const r = await apiFetch<{ status: 'ok'; quiz: QuizMeta; questions: AdminQuestion[] }>(`/quizzes/admin/${id}`);
  return { quiz: r.quiz, questions: r.questions };
}

export async function adminCreateQuiz(title: string): Promise<number> {
  const r = await apiFetch<{ status: 'ok'; id: number }>('/quizzes/admin', {
    method: 'POST',
    body: JSON.stringify({ title }),
  });
  return r.id;
}

export async function adminUpdateQuizTitle(id: number, title: string): Promise<void> {
  await apiFetch(`/quizzes/admin/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

export async function adminDeleteQuiz(id: number): Promise<void> {
  await apiFetch(`/quizzes/admin/${id}`, { method: 'DELETE' });
}

export async function adminReplaceQuestions(id: number, questions: AdminQuestion[]): Promise<void> {
  await apiFetch(`/quizzes/admin/${id}/questions`, {
    method: 'PUT',
    body: JSON.stringify({ questions }),
  });
}
